/**
 * WHO OWNS Ctrl/Cmd+K HERE (#2328 §7, "Context ownership").
 *
 * §7: *"The topmost active routed workspace owns `Ctrl/Cmd+K`. Opening Settings
 * Studio swaps command providers atomically; closing restores the application
 * provider."*
 *
 * ── ONE ANSWER, DERIVED FROM THE ROUTE ─────────────────────────────────────
 * The owner is a pure function of the pathname, and that is what makes the swap
 * ATOMIC rather than merely fast. The Settings Studio is a ROUTE (ruling 74071
 * §3), so "is it open" is a question the router already answers; the alternative
 * — a push/pop owner stack driven by mount effects — swaps one or more frames
 * after the address changes, and a keystroke inside that window goes to the
 * previous owner. It also admits a state nothing can reconcile: an owner pushed
 * by a component that unmounted without popping.
 *
 * `AppShell` consults this and DECLINES the key on a Studio route. The Studio's
 * own handler carries no matching guard, and that asymmetry is measured rather
 * than sloppy: a browser mutation removing the Studio's guard came back GREEN,
 * because `router.tsx` mounts `SettingsStudio` at exactly two addresses and both
 * are Studio addresses — so the predicate could never decline there. An inert
 * declaration is worse than none. `settings/settingsPalette.test.ts` asserts the
 * fact that replaces it instead.
 *
 * What the arbiter buys is still the thing that matters: two `window` keydown
 * handlers resolve by REGISTRATION ORDER, so without the shell declining, the
 * Studio mounted inside it would win or lose by mount timing. Browser arm 26
 * removes the shell's guard and both palettes open.
 *
 * ── WHAT THIS FILE MAY NOT BECOME ──────────────────────────────────────────
 * It is deliberately NOT imported by `providers.ts`. The application palette's
 * provider composition must not know the Settings Studio exists — that is the
 * property `imports.census.test.ts` asserts — and the ownership question belongs
 * to the HOST that mounts a palette, not to the list of things a palette reads.
 * Keeping the two apart is what lets the census be an identity rather than an
 * identity-with-exceptions.
 *
 * ── MUTATION (skill rule 10) — the table is in `providers.test.ts` ─────────
 */
import { isStudioPath } from '../../settings/pages'

/**
 * The two palettes #2328 §7 calls *"physically separate"*.
 *
 * A closed union rather than a boolean: a third routed workspace with its own
 * palette adds a member here and a branch below, and every consumer's switch
 * goes red until it has decided what to do about it. `studioOpen ? a : b`
 * scattered across two hosts would not.
 */
export type PaletteOwner = 'application' | 'settings'

/**
 * Which palette this address opens. TOTAL — every pathname has an owner.
 *
 * `isStudioPath` is the Studio's own predicate, not a second reading of the
 * address: `AppShell` already uses it to decide what to make `inert`, so the
 * shell that is unreachable and the palette that is unavailable are the same
 * fact asked once. A second `startsWith('/settings')` here would be a fork, and
 * it would disagree the day the Studio's route form changes.
 *
 * It is imported from `settings/pages.ts`, the leaf that owns `SETTINGS_ROUTE`,
 * and NOT from `SettingsStudio.tsx`. Importing it from the component statically
 * pulled the lazy Studio route into the shell's chunk — Vite reported exactly
 * that on this packet's build — and a route predicate should not drag a
 * workspace behind it.
 */
export function paletteOwnerFor(pathname: string): PaletteOwner {
  return isStudioPath(pathname) ? 'settings' : 'application'
}
